import App from "./App.vue"
import util from "@plugins/util"
import axios from "axios"
import router from "./router/router"
import { createApp, reactive } from "vue"
import { dashboardState } from "./state/dashboard-state"
import { keycloakState } from "./state/keycloak-state"
import 'material-icons/iconfont/material-icons.css'
import './assets/css/styles.css'



const vueApp = createApp(App)

// >--< >--< >--< >--< >--< >--< >--< >--<
// >--< >--< STATE  DECLARATIONS >--< >--<
// >--< >--< >--< >--< >--< >--< >--< >--<
const globalState = reactive({
  systemConfig: undefined,
  isLoading: true,
  response: undefined,
})


const updtInterval = reactive({
  dataInterval: 20 * 60 * 1000, //init/default value, will be replaced by process.env.DATA_UPDATE_INTERVAL
  resourceInterval: 24 * 60 * 60 * 1000, //init/default value, will be replaced by process.env.RESOURCE_UPDATE_INTERVAL
  activeDashboardInterval: 60 * 60 * 1000, //init/default value, will be replaced by process.env.ACTIVE_UPDATE_INTERVAL
})

/**
 * (asynchronous) function that does the very basic setup
 * before the app can be "run()"
 * mainly handles provisions and configuration calls that
 * are needed for the app to function properly or at all
 */
async function setup() {
  // --- APPLICATION PROVISIONS ---
  // provisions are done first, so the state can be populated by
  // and used to configure plugins
  vueApp.provide("util", util)
  vueApp.provide("globalState", globalState)
  vueApp.provide("keycloakState", keycloakState)
  vueApp.provide("updtInterval", updtInterval)
  vueApp.provide("dashboardState", dashboardState)
  // --- --- --- --- --- --- ---

  // --- KEYCLOAK INSTANTIATION ---
  const configResponse = await axios.get(util.middleware() + "/configuration")
  Object.assign(globalState, {
    systemConfig: configResponse.data,
  })

  Object.assign(updtInterval, {
    dataInterval: configResponse.data.dataResourceUpdateInterval.dataInterval,
    resourceInterval: configResponse.data.dataResourceUpdateInterval.resourceInterval,
    activeDashboardInterval : configResponse.data.dataResourceUpdateInterval.activeDashboardInterval
  })

  // --- --- --- --- --- --- ---

  // --- AXIOS SETUP ---
  // we set up axios to e.g. send headers with every request like the KC token
  configureAxios(keycloakState, globalState)
  // --- --- --- --- --- --- ---

  configureRouter(router, keycloakState, globalState);
}


/**
 * 1. takes a responsive keycloak state to send the current token
 *    with everey request.
 *    this does not work when the reference of the property is passed directly
 *    -> "keycloakResponsiveState.instance.token" as param
 *        since the reactivity is not deep!
 *
 * 2. keeps timers for every request that trigger a loading State if the request
 *    takes too long. Adds a timer for every request, removes a time for every response in time
 *
 * @param {reactive<keycloakState>} keycloakResponsiveState reactive keycloak state object
 */
function configureAxios(
  keycloakResponsiveState,
  globalResponsiveState,
  timeBeforeUiBlockInMs = 2000
) {
  const loadingTimerIds = []

  axios.interceptors.request.use((config) => {
    if (keycloakResponsiveState.available) {
      console.log('Bearer added');
      config.headers["Authorization"] =
        "Bearer " + keycloakResponsiveState.instance.token
    }


    const newTimerId = setTimeout(() => {
      globalResponsiveState.isLoading = true
    }, timeBeforeUiBlockInMs)

    loadingTimerIds.push(newTimerId)

    // pass timeId to config, so that it is available in the response
    config.timerId = newTimerId
    // also pass time of start so wen can check if the timer triggered
    config.timerStart = Date.now()

    return config
  })

  axios.interceptors.response.use(
    (response) => {
      responseTimerHandler(response)
      return response
    },
    (error) => {
      if (error.response) {
        responseTimerHandler(error.response)
      } else {
        responseTimerHandler(error)
      }
      throw error
    }
  )

  // handles timers from resposnes
  const responseTimerHandler = (response) => {
    // cancel the timer
    // and if triggered reset the loading state
    const timerIdToCancel = response.config.timerId
    const timerIndex = loadingTimerIds.indexOf(timerIdToCancel)
    const timerStart = response.config.timerStart
    const now = Date.now()

    const timerTriggered = now - timerStart > timeBeforeUiBlockInMs

    if (timerTriggered) {
      // if the timer was triggered, reset the loading screen
      globalResponsiveState.isLoading = false
    }

    // remove the timer id from the list and clear timeout
    loadingTimerIds.splice(timerIndex, 1)
    clearTimeout(timerIdToCancel)

    // follow the response chain
  }
}



function configureRouter(
  router,
  keycloakResponsiveState,
  globalResponsiveState
) {
  router.beforeEach((to, from, next) => {
    if (to.meta.requiresAuth) {
      keycloakResponsiveState.getInstance(globalResponsiveState.systemConfig.keycloak)
      .then((keycloakInstance) => {
        if (!keycloakInstance.authenticated) {
          keycloakState.instance.login({ redirectUri: window.location + to.path })
        } else {
          next()
        }
      });
    } else {
      next()
    }

  })
}

/**
 * (asynchronous) function that is called after the app is "run()"
 */


/**
 * (asynchronous) function that is called after the basic "setup()" is done
 * represents the acutal app start
 */



async function run() {
  // --- REGISTER COMPLETE PLUGINS ---
  vueApp.use(router)
  // --- --- --- --- --- --- ---

  // --- MOUNT THE APP --- (actual app start)
  vueApp.mount("#app")

  return true
}
// >--< >--< >--< >--< >--< >--< >--< >--<

// >--< >--< >--< >--< >--< >--< >--< >--<
// >--< APP START  PROMISE CHAIN >--< >--<
// >--< >--< >--< >--< >--< >--< >--< >--<
setup()
  .then(() => {
    return run()
  })
// >--< >--< >--< >--< >--< >--< >--< >--<
